import { useState, useEffect, useRef } from 'react';
import { Button, Input, Modal, Switch, Label } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useCreateGateway, useUpdateGateway } from '@/hooks/useGateways';
import type { Gateway } from '@/types';

const STRIP_SUFFIXES = ['mcp gateway', 'mcp server', 'gateway', 'server', 'mcp'];

function deriveGroupFromName(name: string): string {
  let result = name.trim();
  const lower = result.toLowerCase();
  for (const suffix of STRIP_SUFFIXES) {
    if (lower.endsWith(suffix) && lower.length > suffix.length) {
      result = result.slice(0, -suffix.length).trim();
      break;
    }
  }
  return result;
}

interface CreateGatewayModalProps {
  isOpen: boolean;
  onClose: () => void;
  gateway?: Gateway;
  onCreated?: (gateway: Gateway) => void;
}

export default function CreateGatewayModal({
  isOpen,
  onClose,
  gateway,
  onCreated,
}: CreateGatewayModalProps) {
  const isEdit = !!gateway;
  const { success, error: showError } = useToast();
  const createGateway = useCreateGateway();
  const updateGateway = useUpdateGateway();

  const [formData, setFormData] = useState({
    name: '',
    endpoint: '',
    region: '',
    enabled: true,
    defaultGroup: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const regionAutoFilled = useRef(false);
  const groupAutoFilled = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (gateway) {
        setFormData({
          name: gateway.name,
          endpoint: gateway.endpoint,
          region: gateway.region || '',
          enabled: gateway.enabled,
          defaultGroup: gateway.defaultGroup || '',
        });
      } else {
        setFormData({ name: '', endpoint: '', region: '', enabled: true, defaultGroup: '' });
      }
      setErrors({});
      regionAutoFilled.current = false;
      groupAutoFilled.current = false;
    }
  }, [isOpen, gateway]);

  const handleNameChange = (name: string) => {
    const update: typeof formData = { ...formData, name };
    if (formData.defaultGroup === '' || groupAutoFilled.current) {
      update.defaultGroup = deriveGroupFromName(name);
      groupAutoFilled.current = true;
    }
    setFormData(update);
  };

  const AWS_REGION_RE = /\b((?:us|eu|ap|sa|ca|me|af|il)-(?:north|south|east|west|central|northeast|southeast|northwest|southwest)-\d)\b/;

  const handleEndpointChange = (url: string) => {
    const update: typeof formData = { ...formData, endpoint: url };
    const match = url.match(AWS_REGION_RE);
    if (match && (formData.region === '' || regionAutoFilled.current)) {
      update.region = match[1];
      regionAutoFilled.current = true;
    }
    setFormData(update);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.endpoint.trim()) {
      newErrors.endpoint = 'Endpoint URL is required';
    } else {
      try {
        new URL(formData.endpoint);
      } catch {
        newErrors.endpoint = 'Must be a valid URL';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const input = {
      name: formData.name.trim(),
      endpoint: formData.endpoint.trim(),
      region: formData.region.trim() || undefined,
      enabled: formData.enabled,
      defaultGroup: formData.defaultGroup.trim() || undefined,
    };

    try {
      if (isEdit) {
        await updateGateway.mutateAsync({ gatewayId: gateway!.gatewayId, input });
        success('Gateway updated successfully');
      } else {
        const created = await createGateway.mutateAsync(input);
        success('Gateway created successfully');
        onCreated?.(created);
      }
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save gateway');
    }
  };

  const isPending = createGateway.isPending || updateGateway.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Gateway' : 'Add Gateway'}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? 'Saving...' : isEdit ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          placeholder="My MCP Gateway"
          value={formData.name}
          onChange={(e: { target: { value: string } }) => handleNameChange(e.target.value)}
          error={errors.name}
        />

        <Input
          label="Endpoint URL"
          placeholder="https://mcp.example.com"
          value={formData.endpoint}
          onChange={(e: { target: { value: string } }) => handleEndpointChange(e.target.value)}
          error={errors.endpoint}
        />

        <Input
          label="Region"
          placeholder="us-east-1 (optional)"
          value={formData.region}
          onChange={(e: { target: { value: string } }) => {
            regionAutoFilled.current = false;
            setFormData({ ...formData, region: e.target.value });
          }}
          hint="AWS region where the MCP server is hosted"
        />

        <div className="flex items-center justify-between">
          <Label>Enabled</Label>
          <Switch
            checked={formData.enabled}
            onCheckedChange={(checked: boolean) =>
              setFormData({ ...formData, enabled: checked })
            }
          />
        </div>

        <Input
          label="Default Tool Group"
          placeholder="e.g. Ops Tools, Analytics"
          value={formData.defaultGroup}
          onChange={(e: { target: { value: string } }) => {
            groupAutoFilled.current = false;
            setFormData({ ...formData, defaultGroup: e.target.value });
          }}
          hint="Group assigned to synced tools that don't match a known pattern"
        />
      </div>
    </Modal>
  );
}
